<template>
    <header :class="{
        'closed-menu': !isMenuOpen,
        'menu-on-hover': isMenuOnHover,
        'is-site-loading': isSiteLoading
    }">
        <div class="main">
            <span class="logo">
                <IconBase width="127" height="32" :iconColor="consts.COLORS.WHITE"
                          iconName="companyLogoIcon">
                </IconBase>
            </span>
            <span @click.stop="toggleMenu" class="icon-menu-wrap">
                <span class="menu-icon menu-handle">
                    <IconBase width="18" height="15" :iconColor="handleColor"
                              iconName="menuHandleIcon"
                    >
                    </IconBase>
                </span>

                <span class="menu-icon close-icon">
                    <IconBase width="14" height="14" :iconColor="consts.COLORS.WHITE"
                              iconName="closeIcon"
                    >
                    </IconBase>
                </span>
            </span>
            <span class="menus">
                <User></User>
            </span>
            <div class="actions" v-if="showAction">
                <button @click="$emit('actionClicked')"
                        type="button"
                        class="primary small"
                        :disabled="actionDisabled">{{ actionTitle }}</button>
            </div>
        </div>
        <div class="title-section">
            <span class="title">
                {{ pageTitle }}
            </span>
            <component :is="controlsComponent"
                       v-bind="controlsComponentProps"
                       v-dynamic-events="controlsComponentEvents"
                       class="control-component"/>
        </div>
        <Loader :isLoading="isSiteLoading"></Loader>
    </header>
</template>

<script>
import IconBase from 'components/IconBase.vue';
import consts from 'services/metaData/consts';
import { mapMutations, mapGetters } from 'vuex';
import eventBus from '@/mixins/eventBus';
import DateRange from 'components/dateRange/DateRange.vue';
import DynamicEvents from 'directives/dynamicEvents';
import User from 'components/layout/user/User.vue';
import Loader from './Loader.vue';

export default {
    name: 'TopHeader',
    components: {
        User,
        Loader,
        IconBase,
        DateRange
    },
    mixins: [
        eventBus('top-header')
    ],
    data() {
        return {
            consts,
            uniqueId: 'topHeader'
        };
    },
    methods: {
        toggleMenu() {
            this.setIsMenuOpen(!this.isMenuOpen);
        },
        updateRouter() {
            const currentPath = window.location.pathname.replace('/partners', '');
            if (this.$route.path !== currentPath) {
                const search = window.location.search.substring(1);
                const query = search ? JSON.parse(
                    `{"${search.replace(/&/g, '","').replace(/=/g,'":"')}"}`,
                    (key, value) => (key === '' ? value : decodeURIComponent(value))
                ) : {};
                this.$router.resolve({ path: currentPath, query });
            }
        },
        ...mapMutations([
            'setIsMenuOpen',
            'setIsSiteLoading'
        ])
    },
    computed: {
        handleColor() {
            // tablets + small laptops
            const isMediumScreens = this.windowWidth < consts.RESPONSIVE_WIDTH_STEPS.LARGE_LAPTOP
                && this.windowWidth > consts.RESPONSIVE_WIDTH_STEPS.MOBILE;
            return isMediumScreens ? consts.COLORS.DARK_300 : consts.COLORS.WHITE;
        },
        ...mapGetters([
            'pageTitle',
            'isMenuOpen',
            'actionTitle',
            'actionDisabled',
            'showAction',
            'controlsComponent',
            'controlsComponentProps',
            'controlsComponentEvents',
            'isMenuOnHover',
            'isSiteLoading',
            'windowWidth'
        ])
    },
    mounted() {
        if (!window.sspStores) {
            window.sspStores = {};
        }
        window.sspStores.layoutState = this.$store;
        this.setIsSiteLoading(true);
    },
    directives: {
        DynamicEvents
    }
};
</script>

<style scoped lang="scss">
@import "../common";
@import "~styles/buttons";
@import "~styles/functions";

$rowHeightMiniMode: 25px;
// shadow affect on layout header, overlays the green of the header for the user/help menus
$headerMenuBackground: rgba(19, 37, 67, 0.15);
$gradientBlue: #007DFF;
$gradientGreen: #00DFE5;
$menuHandleIconHeight: 12px;
$menuHandleIconHeightClosed: 14px;

header {
    transition: all $menuAnimations;
    width: calc(100% - #{$mainMenuWidth});
    overflow: visible;
    display: inline-block;
    vertical-align: top;
    position: relative;

    &.closed-menu {
        width: calc(100% - #{$mainMenuClosedWidth});

        &.menu-on-hover{
            width: calc(100% - #{$mainMenuWidth});
            margin-left: $mainMenuWidth - $mainMenuClosedWidth;
        }
    }

    &.is-site-loading {
        z-index: 1;
    }

    @media #{$tabletAndBelow} {
        width: 100%;
        &.closed-menu {
            width: 100%;
        }
    }
}

.main {
    background-image: linear-gradient(to right, $gradientBlue, $gradientGreen);
    height: $headerHeight;
    position: relative;

    @media #{$laptop} {
        height: $headerHeightMiniMode;
    }

    @media #{$tablet} {
        height: 0;
    }
}
.title-section {
    @include font(1);
    color: $dark-300;
    background-color: $white;
    height: $pageTitleHeight;
    padding: 20px;
    box-shadow: 0 -4px 3px 0 rgba(0, 0, 0, 0.1);
    border-bottom: $border;

    @media #{$tabletAndBelow} {
        box-shadow: none;
    }

    @media #{$desktopAndAbove} {
        position: relative;
    }
}

.page-sub-title{
    @include font(2);
}

.closed-menu.menu-on-hover .title {
    padding-left: 5px;
    @media #{$largeLaptopAndBelow} {
        padding-left: 28px;
    }
}

.closed-menu .title {
    @media #{$tablet} {
        padding-left: 28px;
    }
    @media #{$mobile} {
        padding-left: 0;
    }
}
.title {
    padding-left: 0;
    transition: padding-left $menuAnimations;

    @media #{$largeLaptopAndBelow} {
        padding-left: 28px;
    }
    @media #{$tablet} {
        padding-left: $mainMenuWidth + 28px;
    }
    @media #{$mobile} {
        padding-left: 0;
    }
}

.closed-menu .close-icon {
    display: none;
}
.close-icon {
    display: none;
    @media #{$mobile} {
        display: inline-block;
    }
}

.closed-menu.menu-on-hover .icon-menu-wrap {
    left: 20px;
    @media #{$desktopAndAbove} {
        left: 2px;
    }
}
.closed-menu .icon-menu-wrap {
    top: 1px;
    left: 1px;

    @media #{$largeLaptopAndBelow} {
        top: 7px;
        left: 20px;
    }
    @media #{$tablet} {
        left: 20px;
        top: 2px;
    }

    @media #{$mobile} {
        top: 7px;
        left: initial;
    }
}
.icon-menu-wrap {
    display: inline-block;
    transition: all $menuAnimations;
    z-index: getZIndexLayerOffset(application,1);
    position: absolute;
    top: 0;
    left: 0;

    @media #{$largeLaptopAndBelow} {
        top: 10px;
        left: 20px;
    }

    @media #{$tablet} {
        top: 3px;
        left: $mainMenuWidth + 20px;
    }

    @media #{$mobile} {
        top: 10px;
        right: 20px;
        transition: unset;
    }
}

.closed-menu .menu-icon {
    transform: rotate(90deg);
    @media #{$mobile} {
        float: right;

        &.menu-handle {
            display: initial;
        }
    }
}
.menu-icon {
    margin-top: $headerHeight/2 - $menuHandleIconHeight/2;
    cursor: pointer;
    float: left;
    @media #{$mobile} {
        margin-top: 9px;
        float: right;
    }
    @media #{$desktopAndAbove} {
        margin-top: 16px;
    }

    &.menu-handle {
        transition: all 450ms ease-in;

        @media #{$mobile} {
            display: none;
        }
    }
}


.logo {
    display: none;
    margin-top: $headerHeight/2 - 15px;
    margin-left: 15px;

    @media #{$mobile} {
        display: inline-block;
    }
}

.actions {
    height: $headerHeight;
    line-height: $headerHeight;
    margin-top: $headerHeight;
    float: right;

    @media #{$desktopAndAbove}{
        position: absolute;
        right: 27px;
        vertical-align: middle;
        line-height: $pageTitleHeight;
        height: $pageTitleHeight;
        z-index: 1;
    }
    @media #{$largeLaptopAndBelow} {
        height: $rowHeightMiniMode;
        line-height: $rowHeightMiniMode;
        margin-top: 21px;
        padding-right: 15px;
        border-right: 1px solid rgba($light-800,0.3);
    }
    @media #{$tablet} {
        margin-top: $pageTitleHeight/2 - $rowHeightMiniMode/2;
    }
    @media #{$mobile} {
        display: none;
    }
}

.menus {
    background-color: $headerMenuBackground;
    height: $headerHeight;
    line-height: $headerHeight;
    position: relative;
    color: $white;
    float: right;

    @media #{$largeLaptopAndBelow} {
        background-color: transparent;
        height: $rowHeightMiniMode;
        line-height: $rowHeightMiniMode;
        margin-top: 22px;
    }

    @media #{$tablet} {
        margin-top: $pageTitleHeight/2 - $rowHeightMiniMode/2;
    }

    @media #{$mobile} {
        display: none;
    }
}
.control-component {
    display: inline-block;
    vertical-align: middle;
    height: 22px;
    margin-top: -2px;
}

</style>
